import { useTranslation } from 'react-i18next'
import { formatAmount, formatCLP } from './format'

/**
 * One budget as a bar: spent, then committed, against the budget.
 *
 * `percent` is null when nobody has set a budget, which is a different thing
 * from 0% used — the bar renders empty and greyed rather than pretending the
 * project is on track.
 */
export function BudgetBar({ side, kind, compact = false }) {
  const { t } = useTranslation('engineering')
  const { budget, used, remaining, percent, over } = side
  const spent = kind === 'money' ? side.spent : used
  const committed = kind === 'money' ? side.committed : 0

  const hasBudget = budget != null && budget > 0
  const scale = hasBudget ? budget : Math.max(used, 1)
  const pctOf = (v) => Math.min(100, (v / scale) * 100)

  const spentColor = over ? '#dc2626' : '#0f766e'
  const committedColor = over ? '#f87171' : '#5eead4'

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      <div className="flex items-baseline justify-between gap-3">
        <span className={`font-medium text-gray-700 ${compact ? 'text-xs' : 'text-sm'}`}>
          {t(kind === 'money' ? 'budget.money' : 'budget.hours')}
        </span>
        <span className={`tabular-nums ${compact ? 'text-xs' : 'text-sm'} ${
          over ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
          {formatAmount(used, kind, { compact })}
          {' / '}
          {hasBudget ? formatAmount(budget, kind, { compact })
                     : <span className="text-gray-400">{t('budget.noBudget')}</span>}
          {percent != null && <span className="ml-1.5 text-gray-400">{percent}%</span>}
        </span>
      </div>

      <div className={`w-full rounded-full overflow-hidden bg-gray-100 ${
        compact ? 'h-1.5' : 'h-2.5'}`}>
        <div className="h-full flex">
          <div style={{ width: `${pctOf(spent)}%`, background: spentColor }} />
          {committed > 0 && (
            <div style={{ width: `${pctOf(committed)}%`, background: committedColor }} />
          )}
        </div>
      </div>

      {!compact && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
          {kind === 'money' && (
            <>
              <Dot color={spentColor} label={`${t('budget.spent')} ${formatCLP(side.spent)}`} />
              <Dot color={committedColor}
                   label={`${t('budget.committed')} ${formatCLP(side.committed)}`} />
            </>
          )}
          <span className={over ? 'text-red-600 font-medium' : 'text-gray-700 font-medium'}>
            {over
              ? t('budget.overBy', { amount: formatAmount(Math.abs(remaining), kind) })
              : remaining != null
                ? `${t('budget.remaining')} ${formatAmount(remaining, kind)}`
                : t('budget.noBudgetHint')}
          </span>
        </div>
      )}
    </div>
  )
}

function Dot({ color, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  )
}

export default BudgetBar
